import { supabase, isSupabaseConfigured, resolveFunctionError } from '@/lib/supabase';

// =============================================================
// Pasarela de financiamiento Sistecrédito (Compra a Cuotas)
// =============================================================
// La arquitectura y el flujo de integración quedan CABLEADOS desde ya, listos
// para activarse cuando termine el enrolamiento. Mientras `sistecredito_enabled`
// siga en false, la tienda NO ofrece el método como seleccionable.
//
// Las credenciales viven en la tabla `gateways_config` (RLS exclusivo del rol
// Admin). La tienda pública SOLO lee la vista `gateways_config_public`, que
// expone `sistecredito_enabled` y `sistecredito_test_mode` (nunca el vendor_id,
// la api_key ni el client_secret, que solo usan las Edge Functions).

export interface SistecreditoPublicConfig {
  enabled: boolean;
  isTestMode: boolean;
  // `configured` = está activada Y con credenciales mínimas (vendor_id). Sin
  // esto no se debe mostrar como método seleccionable.
  configured: boolean;
}

export interface SistecreditoAdminConfig {
  vendorId: string;
  apiKey: string;
  clientSecret: string;
  isTestMode: boolean;
  enabled: boolean;
}

const EMPTY_PUBLIC: SistecreditoPublicConfig = {
  enabled: false,
  isTestMode: true,
  configured: false,
};

// Consulta la configuración pública de Sistecrédito (segura para la tienda).
// Solo lee columnas no sensibles a través de la vista `gateways_config_public`.
export async function getSistecreditoPublicConfig(): Promise<SistecreditoPublicConfig> {
  if (!isSupabaseConfigured || !supabase) return { ...EMPTY_PUBLIC };
  try {
    const { data, error } = await supabase
      .from('gateways_config_public')
      .select('sistecredito_enabled, sistecredito_test_mode')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...EMPTY_PUBLIC };
    const row = data as Record<string, unknown>;
    const enabled =
      row.sistecredito_enabled === undefined || row.sistecredito_enabled === null
        ? false
        : Boolean(row.sistecredito_enabled);
    const isTestMode =
      row.sistecredito_test_mode === undefined || row.sistecredito_test_mode === null
        ? true
        : Boolean(row.sistecredito_test_mode);
    return {
      enabled,
      isTestMode,
      // La vista pública no expone el vendor_id, así que `configured` solo se
      // vuelve true cuando el administrador activa explícitamente la pasarela
      // (el Edge Function igual valida las credenciales reales antes de cobrar).
      configured: enabled,
    };
  } catch (err) {
    console.warn('[sistecredito] getSistecreditoPublicConfig failed:', err);
    return { ...EMPTY_PUBLIC };
  }
}

// ⚠️ SOLO ADMINISTRADOR: lee la configuración completa (incluidas las llaves)
// para el panel de administración. La protección real la aplica RLS (is_admin()).
export async function fetchSistecreditoAdminConfig(): Promise<SistecreditoAdminConfig> {
  const empty: SistecreditoAdminConfig = {
    vendorId: '',
    apiKey: '',
    clientSecret: '',
    isTestMode: true,
    enabled: false,
  };
  if (!isSupabaseConfigured || !supabase) return empty;
  const { data, error } = await supabase
    .from('gateways_config')
    .select('sistecredito_vendor_id, sistecredito_api_key, sistecredito_client_secret, sistecredito_test_mode, sistecredito_enabled')
    .limit(1)
    .maybeSingle();
  if (error || !data) return empty;
  const row = data as Record<string, unknown>;
  return {
    vendorId: String(row.sistecredito_vendor_id ?? ''),
    apiKey: String(row.sistecredito_api_key ?? ''),
    clientSecret: String(row.sistecredito_client_secret ?? ''),
    isTestMode:
      row.sistecredito_test_mode === undefined || row.sistecredito_test_mode === null
        ? true
        : Boolean(row.sistecredito_test_mode),
    enabled:
      row.sistecredito_enabled === undefined || row.sistecredito_enabled === null
        ? false
        : Boolean(row.sistecredito_enabled),
  };
}

// Guarda (upsert) la configuración de Sistecrédito desde el panel de admin.
export async function saveSistecreditoAdminConfig(
  patch: Partial<SistecreditoAdminConfig>,
): Promise<void> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase no está configurado');
  }
  const row: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.vendorId !== undefined) row.sistecredito_vendor_id = patch.vendorId.trim();
  if (patch.apiKey !== undefined) row.sistecredito_api_key = patch.apiKey.trim();
  if (patch.clientSecret !== undefined) row.sistecredito_client_secret = patch.clientSecret.trim();
  if (patch.isTestMode !== undefined) row.sistecredito_test_mode = patch.isTestMode;
  if (patch.enabled !== undefined) row.sistecredito_enabled = patch.enabled;

  const { data: existing } = await supabase
    .from('gateways_config')
    .select('id')
    .limit(1)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase.from('gateways_config').update(row).eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase.from('gateways_config').insert(row);
    if (error) throw new Error(error.message);
  }
}

// -------------------------------------------------------------
// Inicio del checkout de financiamiento
// -------------------------------------------------------------

export interface SistecreditoCustomer {
  name: string;
  document: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  department: string;
}

export interface SistecreditoCheckoutPayload {
  orderId: string;
  orderNumber: string;
  amount: number;
  customer: SistecreditoCustomer;
  // Número de cuotas deseado por el cliente (opcional; Sistecrédito ofrece su
  // propio simulador en su checkout, pero podemos enviar una preferencia).
  installments?: number;
  // URL a la que Sistecrédito devuelve al cliente tras finalizar su solicitud.
  returnUrl?: string;
}

export interface SistecreditoCheckoutResult {
  // URL a la que se debe redirigir/abrir el cliente para completar la solicitud
  // de crédito. Puede venir vacía si la pasarela responde solo con token.
  redirectUrl: string;
  // Token/sesión devuelto por Sistecrédito para abrir su modal/checkout.
  token: string;
  status: string;
  enabled: boolean;
}

// URL a la que Sistecrédito retorna al cliente al terminar su solicitud. La
// confirmación AUTORITATIVA del pago llega por el webhook del servidor, no por
// aquí; esta vuelta solo sirve para que el cliente vea el estado de su pedido.
export function getSistecreditoReturnUrl(orderNumber: string): string {
  const basePath = (__BASE_PATH__ || '').replace(/\/+$/, '');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${basePath}/pedidos?pedido=${encodeURIComponent(orderNumber)}`;
}

// Inicia la solicitud de crédito a través de la Edge Function
// `sistecredito-checkout`, que lee las credenciales del servidor (nunca del
// navegador), consulta la API de Sistecrédito y devuelve la URL/token de
// redirección. Nunca expone las llaves privadas al cliente.
export async function startSistecreditoCheckout(
  payload: SistecreditoCheckoutPayload,
): Promise<SistecreditoCheckoutResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }
  const { data, error } = await supabase.functions.invoke('sistecredito-checkout', {
    body: payload,
  });
  if (error) {
    throw new Error(await resolveFunctionError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return {
    redirectUrl: String(data?.redirectUrl ?? ''),
    token: String(data?.token ?? ''),
    status: String(data?.status ?? ''),
    enabled: Boolean(data?.enabled),
  };
}

// -------------------------------------------------------------
// Regenerar / reabrir el enlace de financiamiento de un pedido pendiente
// -------------------------------------------------------------
// Cuando el cliente dejó a medias la solicitud de Sistecrédito (o perdió el
// enlace), se puede volver a pedir la URL de su checkout para el MISMO pedido,
// sin crear uno nuevo. La verificación la hace el servidor.
export interface ReopenFinancingResult {
  redirectUrl: string;
  token: string;
  status: string;
  enabled: boolean;
}

export async function reopenSistecreditoFinancing(input: {
  orderNumber: string;
  // Dato de titularidad (celular/correo/documento). Obligatorio desde la tienda
  // pública; opcional desde el panel de administración.
  contact?: string;
  // Alternativa al número (uso interno del panel).
  orderId?: string;
}): Promise<ReopenFinancingResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }
  const { data, error } = await supabase.functions.invoke('reopen-sistecredito-financing', {
    body: {
      orderNumber: input.orderNumber,
      orderId: input.orderId ?? '',
      contact: input.contact ?? '',
      returnUrl: input.orderNumber ? getSistecreditoReturnUrl(input.orderNumber) : '',
    },
  });
  if (error) {
    throw new Error(await resolveFunctionError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return {
    redirectUrl: String(data?.redirectUrl ?? ''),
    token: String(data?.token ?? ''),
    status: String(data?.status ?? ''),
    enabled: Boolean(data?.enabled),
  };
}

// -------------------------------------------------------------
// Venta directa financiada (Panel Admin)
// -------------------------------------------------------------
// Registra una venta directa con Sistecrédito SIN descontar inventario todavía:
// crea un pedido en 'pending_payment' (con reserva de stock y pago pendiente) y
// devuelve el enlace de financiamiento. La venta y el descuento de inventario se
// confirman recién cuando Sistecrédito aprueba, vía webhook.

export interface SistecreditoDirectSaleItem {
  variantId: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
}

export interface SistecreditoDirectSaleShipping {
  recipientName: string;
  recipientPhone: string;
  recipientIdNumber: string;
  recipientEmail: string;
  department: string;
  city: string;
  address: string;
  notes: string;
}

export interface SistecreditoDirectSaleInput {
  customerId: string;
  salespersonId?: string;
  items: SistecreditoDirectSaleItem[];
  notes?: string;
  shipping?: SistecreditoDirectSaleShipping;
  shippingCost?: number;
}

export interface SistecreditoDirectSaleResult {
  orderNumber: string;
  redirectUrl: string;
}

export async function createSistecreditoDirectSale(
  input: SistecreditoDirectSaleInput,
): Promise<SistecreditoDirectSaleResult> {
  if (!supabase) {
    throw new Error('Supabase no está configurado');
  }

  // 1) Datos del cliente (cédula, contacto) para la solicitud de crédito.
  const { data: customer, error: custErr } = await supabase
    .from('customers')
    .select('first_name, last_name, phone, email, address, city, department, document_id')
    .eq('id', input.customerId)
    .maybeSingle();
  if (custErr) throw new Error(custErr.message);
  if (!customer) throw new Error('No se encontró el cliente.');
  const c = customer as Record<string, any>;

  // 2) Crear el pedido de financiamiento (order + reserva de stock + pago pendiente).
  const { data: created, error: createErr } = await supabase.functions.invoke(
    'create-direct-financing-order',
    {
      body: {
        customerId: input.customerId,
        salespersonId: input.salespersonId ?? '',
        items: input.items,
        notes: input.notes ?? '',
        shipping: input.shipping ?? null,
        shippingCost: input.shippingCost ?? 0,
      },
    },
  );
  if (createErr) throw new Error(await resolveFunctionError(createErr));
  if (created?.error) throw new Error(created.error);

  const orderId = String(created?.orderId ?? '');
  const orderNumber = String(created?.orderNumber ?? '');
  const amount = Number(created?.total ?? 0);
  if (!orderId || !orderNumber) {
    throw new Error('No se pudo crear el pedido de financiamiento.');
  }

  // 3) Iniciar la solicitud de crédito y obtener la URL de checkout.
  const result = await startSistecreditoCheckout({
    orderId,
    orderNumber,
    amount,
    returnUrl: getSistecreditoReturnUrl(orderNumber),
    customer: {
      name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
      document: String(c.document_id ?? ''),
      phone: String(c.phone ?? ''),
      email: String(c.email ?? ''),
      address: String(c.address ?? input.shipping?.address ?? ''),
      city: String(c.city ?? input.shipping?.city ?? ''),
      department: String(c.department ?? input.shipping?.department ?? ''),
    },
  });

  return { orderNumber, redirectUrl: result.redirectUrl };
}